import { Paper } from '@mui/material'
import CustomButton from './v2/general/CustomButton/CustomButton'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import { useIsTimesheetApproved, useIsTimesheetReadyForEdit } from '../utils/hooks'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../store/store'
import {
    acceptApproval,
    ApprovalRequest,
    rejectApproval,
    WorktimesRequest,
} from '../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { approvalLoading } from '../store/slices/function/timesheetLoaderSlice'
import { addDetailsDriver, handleDriver } from '../store/slices/timesheet/timesheetPageDetailSlice'
import { handleResponse, handleError } from '../utils/errorHandler'
import { changeFeedbackOption } from '../store/slices/function/feedback'
import React, { useEffect, useMemo, useState } from 'react'
import {
    useTimesheetApprovalMutation,
    useTimesheetEditMutation,
    useTimesheetEditTimesheetMutation,
    useTimesheetPrepareForEditMutation,
    useTimesheetRejectMutation,
} from '../store/api/timesheet/timesheetApi'
import { mapServicesForApproval, mapServicesForEdit } from '../utils/timesheet/services'
import { filterWorkTimes } from '../utils/timesheet/workTimes'
import { BonusCode, Services } from '../store/slices/timesheet/timesheetPageSlice'
import { TimesheetStoreState } from '../store/timesheetStore'
import { useGetTimesheetQuery } from '../utils/hooks/useGetTimesheetQuery'
import { useGetTimesheetsTotalsQuery } from '../utils/hooks/useGetTimesheetsTotalsQuery'
import { useGetTimesheetsFilteredQuery } from '../utils/hooks/useGetTimesheetsFilteredQuery'
import TimesheetNotificationDialog, {
    type TimesheetNotificationSubmitPayload,
} from './timesheet/TimesheetNotificationDialog'
import { ServiceExecutionUpdateRequest } from '../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'

function ActionButtons() {
    const dispatch = useDispatch<AppDispatch>()

    const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)

    const timesheetApprovalDetailsReject = useSelector(
        (state: TimesheetStoreState) => state.timesheetApprovalDetailsReject,
    )

    const timesheetDetailsRegistryKmsError = useSelector(
        (state: TimesheetStoreState) => state.timesheetDetailsRegistryKmsError,
    )

    const timesheetDetailsRegistryTimesError = useSelector(
        (state: TimesheetStoreState) => state.timesheetDetailsRegistryTimesError,
    )

    const timesheetApprovalDetailsVerification = useSelector(
        (state: TimesheetStoreState) => state.timesheetApprovalDetailsVerification,
    )

    const timesheetPageDetail = useSelector((state: TimesheetStoreState) => state.timesheetPageDetail)

    const timesheetApprovalHasChanges = useSelector((state: TimesheetStoreState) => state.timesheetApprovalHasChanges)

    const { isLoading, isError } = useGetTimesheetQuery()

    const [timesheetApproval] = useTimesheetApprovalMutation()
    const [timesheetEdit, { isLoading: isEditingTimesheetApproved }] = useTimesheetEditMutation()
    const [timesheetReject, { isLoading: isRejectingTimesheet }] = useTimesheetRejectMutation()

    const { refetch: refetchTimesheets } = useGetTimesheetsFilteredQuery()

    const timesheetApprovalDetailsAccept = useSelector(
        (state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
    )

    const timesheetApprovalBenefitCodes = useSelector(
        (state: TimesheetStoreState) => state.timesheetApprovalBenefitCodes,
    )
    const timesheetDriverStatus = useMemo(() => timesheetPageDetail.driver.status, [timesheetPageDetail.driver.status])

    const [isPrimaryDriverNotApproved, setIsPrimaryDriverNotApproved] = useState(false)
    const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState(false)

    const { refetch: refetchTimesheetTotals } = useGetTimesheetsTotalsQuery()

    const isTimeSheetApproved = useIsTimesheetApproved()
    const isTimesheetReadyForEdit = useIsTimesheetReadyForEdit()

    const hasNegativeKms = useMemo(() => {
        const sourceServices: (Services | ServiceExecutionUpdateRequest)[] = isTimeSheetApproved
            ? timesheetApprovalDetailsAccept.services
            : timesheetPage.services || []
        return sourceServices.some(service => {
            const kmsVehicles =
                (service as Services).serviceExecution?.kmsVehicles ||
                (service as ServiceExecutionUpdateRequest).kmsVehicles
            if (!kmsVehicles) return false
            const total = kmsVehicles.reduce((acc, kmv) => acc + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0)
            return total < 0
        })
    }, [isTimeSheetApproved, timesheetApprovalDetailsAccept.services, timesheetPage.services])

    const isEditApprovedButtonDisabled =
        timesheetDetailsRegistryKmsError.registryKmsError ||
        timesheetDetailsRegistryTimesError.registryTimesError ||
        timesheetApprovalDetailsVerification.verification.gratificationError ||
        hasNegativeKms

    const handleOpenModal = () => {
        setIsNotificationDialogOpen(true)
    }

    const handleRejectWithNotification = async ({ title, message }: TimesheetNotificationSubmitPayload) => {
        const timesheetId = timesheetPage?.timeSheetId

        if (!timesheetId || Number(timesheetId) <= 0) {
            throw new Error('Missing timesheet id')
        }

        const rejectPayload = {
            ...timesheetApprovalDetailsReject,
            timesheetId,
            comments: message,
            title,
            message,
        }

        try {
            dispatch(approvalLoading({ loading: true }))
            dispatch(rejectApproval(rejectPayload))

            const response = await timesheetReject(rejectPayload).unwrap()

            handleResponse({ data: response })
            refetchTimesheets()
            refetchTimesheetTotals()
            dispatch(
                addDetailsDriver({
                    driverTableId: timesheetPageDetail.driverTableId,
                    driver: {
                        ...timesheetPageDetail.driver,
                        status: timesheetDriverStatus,
                    },
                }),
            )
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        } catch (error) {
            handleError(error, 'ActionButtons:timesheetReject')
            throw error
        } finally {
            dispatch(approvalLoading({ loading: false }))
        }
    }

    const handleWorkTimes = (): WorktimesRequest[] => {
        const source = isTimeSheetApproved ? timesheetApprovalDetailsAccept.workTimes : timesheetPage.workTimes
        return filterWorkTimes(source)
    }

    const handleServices = async () => {
        return mapServicesForApproval(timesheetPage.services)
    }

    const handleBenefits = async () => {
        const filteredBenefitCodes = Object.keys(timesheetApprovalBenefitCodes?.benefitCodes).filter(item => {
            if (timesheetApprovalBenefitCodes?.benefitCodes[item].isRepeated === true) return Number(item)
        })
        const filteredBenefitCodesNumbers = filteredBenefitCodes.map(item => Number(item))
        const filteredPageBenefitCode = timesheetPage.bonusCodes.map(item => Number(item.codeId))
        if (filteredBenefitCodes.length === 0) {
            return [...filteredPageBenefitCode]
        } else {
            return [...filteredBenefitCodesNumbers]
        }
    }

    const handleApproval = async (isEdit: boolean = false) => {
        try {
            if (timesheetApprovalDetailsVerification.verification.gratificationError === false) {
                if (!isLoading || !isError) {
                    dispatch(approvalLoading({ loading: true }))
                    let approval: ApprovalRequest
                    const deliverPromise = (async () => {
                        approval = {
                            workTimes: handleWorkTimes(),
                            services: await handleServices(),
                            benefits: await handleBenefits(),
                            timesheetId: timesheetPage?.timeSheetId,
                            hasChangesFromReviewer: timesheetApprovalHasChanges.hasChangesFromReviewer,
                            comments: timesheetApprovalDetailsAccept.comments,
                            refund: timesheetApprovalDetailsAccept.refund,
                            workTypeId: isTimeSheetApproved
                                ? timesheetApprovalDetailsAccept.workTypeId
                                : timesheetPage.workTypeId,
                            initialStatus: isTimeSheetApproved
                                ? timesheetApprovalDetailsAccept.initialStatus
                                : (timesheetPage.initialStatus ?? timesheetApprovalDetailsAccept.initialStatus),
                            refunds: timesheetApprovalDetailsAccept.refunds,
                            extraTimePayment: isTimeSheetApproved
                                ? timesheetApprovalDetailsAccept.extraTimePayment
                                : (timesheetPage.extraTimePayment ?? false),
                            dispRecalculateOnSave: timesheetPage.dispRecalculateOnSave ?? true,
                            dispDrivingBreak: timesheetPage.dispDrivingBreak ?? null,
                            uniqueAgent: timesheetPage.uniqueAgent ?? null,
                        }
                        dispatch(acceptApproval({ ...approval }))
                        return 'yes'
                    })()
                    deliverPromise.then(async () =>
                        (isEdit ? timesheetEditTimesheet : timesheetApproval)({ ...approval })
                            .then(response => {
                                handleResponse(response)
                                refetchTimesheets()
                                refetchTimesheetTotals()
                                dispatch(
                                    addDetailsDriver({
                                        driverTableId: timesheetPageDetail.driverTableId,
                                    }),
                                )
                                dispatch(handleDriver({ status: timesheetDriverStatus })) // using the dedicated action handler because we only want to update one property
                                dispatch(approvalLoading({ loading: false }))
                            })
                            .catch(error => {
                                handleError(error, 'ApprovalDetails.tsx')
                                dispatch(approvalLoading({ loading: false }))
                            }),
                    )
                }
            }
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        } catch (error) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            refetchTimesheets()
            handleError(error, 'ApprovalDetails.tsx')
        }
    }

    const timesheetDetailsWorkTimesError = useSelector(
        (state: TimesheetStoreState) => state.timesheetDetailsWorkTimesError,
    )

    const [timesheetPrepareForEdit, { isLoading: isPreparingForEdit }] = useTimesheetPrepareForEditMutation()
    const [timesheetEditTimesheet, { isLoading: isEditingTimesheet }] = useTimesheetEditTimesheetMutation()

    const canPrepareForEdit = useMemo(() => {
        return (
            timesheetPage?.status === 'NotSubmitted' &&
            !isTimeSheetApproved &&
            !timesheetDetailsRegistryKmsError?.registryKmsError &&
            !timesheetDetailsWorkTimesError?.workTimesError &&
            !timesheetDetailsRegistryTimesError?.registryTimesError &&
            !timesheetApprovalDetailsVerification?.verification?.gratificationError &&
            !timesheetPage.driver.canUseApp
        )
    }, [
        timesheetPage?.status,
        isTimeSheetApproved,
        timesheetDetailsRegistryKmsError?.registryKmsError,
        timesheetDetailsWorkTimesError?.workTimesError,
        timesheetDetailsRegistryTimesError?.registryTimesError,
        timesheetApprovalDetailsVerification?.verification?.gratificationError,
    ])

    const handlePrepareForEdit = async () => {
        try {
            if (!canPrepareForEdit || isPreparingForEdit) return
            const timesheetId = timesheetPage?.timeSheetId
            if (!timesheetId || Number(timesheetId) <= 0) return
            dispatch(approvalLoading({ loading: true }))

            await timesheetPrepareForEdit(timesheetId).unwrap()

            // lists to reflect the new ReadyForEdit state
            refetchTimesheets()
            refetchTimesheetTotals()
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        } catch (error) {
            handleError(error, 'ActionButtons:timesheetEditApproval')
            dispatch(
                changeFeedbackOption({
                    id: Math.random().toString(36).substring(7),
                    status: 'error',
                    message: 'Erro ao preparar Folha de Ponto para edição',
                    alert: true,
                }),
            )
        } finally {
            dispatch(approvalLoading({ loading: false }))
        }
    }

    const handleBenefitsEdit = async () => {
        let filteredPageBenefitCode: number[] = []
        const filteredBenefitCodes = Object.keys(timesheetApprovalBenefitCodes?.benefitCodes).filter(item => {
            if (timesheetApprovalBenefitCodes?.benefitCodes[item].isRepeated === true) return Number(item)
        })

        const filteredBenefitCodesNumbers = filteredBenefitCodes.map(item => Number(item))

        if (timesheetPage !== null && Array.isArray(timesheetPage?.approval?.timeSheetApprovalCodes)) {
            filteredPageBenefitCode = timesheetPage?.approval?.timeSheetApprovalCodes
                ? timesheetPage.approval?.timeSheetApprovalCodes.map((item: BonusCode) => Number(item.codeId))
                : []
        } else {
            filteredPageBenefitCode = []
        }

        if (!Array.isArray(filteredBenefitCodes) || filteredBenefitCodes.length === 0) {
            return [...filteredPageBenefitCode]
        } else {
            return [...filteredBenefitCodesNumbers]
        }
    }
    const handleServicesEdit = async () => {
        return mapServicesForEdit(timesheetApprovalDetailsAccept.services)
    }

    const handleEditTimesheet = async () => {
        await handleApproval(true)
    }

    const handleEditApproved = async () => {
        try {
            if (timesheetApprovalDetailsVerification.verification.gratificationError === false) {
                if (!isLoading || !isError) {
                    dispatch(approvalLoading({ loading: true }))
                    let approval: ApprovalRequest
                    const deliverPromise = (async () => {
                        approval = {
                            workTimes: handleWorkTimes(),
                            services: await handleServicesEdit(),
                            benefits: await handleBenefitsEdit(),
                            timesheetId: timesheetPage?.timeSheetId,
                            hasChangesFromReviewer: timesheetApprovalHasChanges.hasChangesFromReviewer,
                            comments: timesheetApprovalDetailsAccept.comments,
                            refund: timesheetApprovalDetailsAccept.refund,
                            workTypeId: timesheetApprovalDetailsAccept.workTypeId,
                            initialStatus: timesheetApprovalDetailsAccept.initialStatus,
                            refunds: timesheetApprovalDetailsAccept.refunds,
                            extraTimePayment: timesheetApprovalDetailsAccept.extraTimePayment,
                            dispRecalculateOnSave: timesheetPage.dispRecalculateOnSave ?? true,
                            dispDrivingBreak: timesheetPage.dispDrivingBreak ?? null,
                            uniqueAgent: timesheetPage.uniqueAgent ?? null,
                        }
                        dispatch(acceptApproval({ ...approval }))
                        return 'yes'
                    })()
                    deliverPromise.then(async () =>
                        timesheetEdit({
                            ...approval,
                        })
                            .then(response => {
                                handleResponse(response)
                                dispatch(
                                    addDetailsDriver({
                                        driverTableId: timesheetPageDetail.driverTableId,
                                    }),
                                )
                                dispatch(handleDriver({ status: timesheetDriverStatus }))
                                dispatch(approvalLoading({ loading: false }))
                            })
                            .catch(error => {
                                handleError(error, 'ApprovalDetails.tsx')
                                dispatch(approvalLoading({ loading: false }))
                            }),
                    )
                }
            }
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        } catch (error) {
            handleError(error, 'ApprovalDetails.tsx')
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            refetchTimesheets()
        }
    }

    const isDisabled = useMemo(() => {
        return (
            (!timesheetPage?.isSubmitted && !isTimesheetReadyForEdit) ||
            isTimeSheetApproved ||
            timesheetDetailsRegistryKmsError?.registryKmsError ||
            timesheetDetailsWorkTimesError?.workTimesError ||
            timesheetDetailsRegistryTimesError?.registryTimesError ||
            timesheetApprovalDetailsVerification?.verification?.gratificationError ||
            hasNegativeKms ||
            (timesheetPage?.isSubmitted && !isTimeSheetApproved && timesheetPage?.status === 'Rejected') // reprovado
        )
    }, [
        timesheetPage?.isSubmitted,
        isTimeSheetApproved,
        timesheetDetailsRegistryKmsError?.registryKmsError,
        timesheetDetailsWorkTimesError?.workTimesError,
        timesheetDetailsRegistryTimesError?.registryTimesError,
        timesheetApprovalDetailsVerification?.verification?.gratificationError,
        timesheetPage?.status,
        hasNegativeKms,
    ])

    const isApprovalDisabled = useMemo(() => {
        return (
            isDisabled ||
            isPrimaryDriverNotApproved ||
            (!isTimeSheetApproved &&
                (timesheetPage.initialStatus?.kmsIni === null || timesheetPage.initialStatus?.regDate === null))
        )
    }, [isDisabled, isPrimaryDriverNotApproved, timesheetPage.initialStatus, isTimeSheetApproved])

    useEffect(() => {
        if (
            timesheetPage.services?.some(
                service =>
                    service.service?.seconddriver &&
                    service.mainSecundaryDriver !== null &&
                    service.mainSecundaryDriverTimeSheetStatus !== 'Approved',
            )
        ) {
            setIsPrimaryDriverNotApproved(true)
        }
    }, [timesheetPage])

    if (isTimeSheetApproved) {
        return (
            <EditButton
                isDisabled={isEditApprovedButtonDisabled || isEditingTimesheetApproved}
                handleEdit={handleEditApproved}
            />
        )
    } else {
        return (
            <>
                <ApprovalButtons
                    isDisabled={isDisabled}
                    isApprovalDisabled={isApprovalDisabled}
                    canPrepareForEdit={canPrepareForEdit}
                    isPrepareDisabled={!canPrepareForEdit || isPreparingForEdit}
                    handlePrepareForEdit={handlePrepareForEdit}
                    handleOpenModal={handleOpenModal}
                    handleApproval={() => handleApproval(false)}
                    canEditTimesheet={isTimesheetReadyForEdit}
                    handleEdit={handleEditTimesheet}
                    isDisabledEdit={isEditingTimesheet}
                    isRefuseDisabled={!timesheetPage.driver.canUseApp}
                />
                <TimesheetNotificationDialog
                    open={isNotificationDialogOpen}
                    onClose={() => setIsNotificationDialogOpen(false)}
                    onConfirm={handleRejectWithNotification}
                    isSubmitting={isRejectingTimesheet}
                />
            </>
        )
    }
}
export default ActionButtons

interface ApprovalButtonsProps {
    isDisabled: boolean
    isApprovalDisabled: boolean
    canPrepareForEdit: boolean
    isPrepareDisabled: boolean
    handleOpenModal: () => void
    handleApproval: () => void
    handlePrepareForEdit: () => void
    canEditTimesheet: boolean
    handleEdit: () => void
    isDisabledEdit: boolean
    isRefuseDisabled: boolean
}
function ApprovalButtons({
    isDisabled,
    isApprovalDisabled,
    canPrepareForEdit,
    isPrepareDisabled,
    handleOpenModal,
    handleApproval,
    handlePrepareForEdit,
    canEditTimesheet,
    handleEdit,
    isDisabledEdit,
    isRefuseDisabled,
}: Readonly<ApprovalButtonsProps>): React.JSX.Element {
    return (
        <Paper
            square
            sx={{
                boxShadow: 'none',
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                backgroundColor: 'transparent',
                marginTop: '0.5rem',
            }}
        >
            {canEditTimesheet && (
                <CustomButton
                    onClick={handleEdit}
                    disabled={isDisabledEdit}
                    variant='contained'
                    color='secondary'
                    size='large'
                >
                    Guardar
                </CustomButton>
            )}
            {canPrepareForEdit && (
                <CustomButton
                    onClick={handlePrepareForEdit}
                    disabled={isPrepareDisabled}
                    variant='contained'
                    color='secondary'
                    size='large'
                >
                    Editar
                </CustomButton>
            )}
            <CustomButton
                onClick={handleOpenModal}
                disabled={isRefuseDisabled || isDisabled}
                endIcon={<CloseIcon />}
                variant='contained'
                color='primary'
                size='large'
            >
                Recusar e Pedir Edição
            </CustomButton>
            <CustomButton
                onClick={handleApproval}
                endIcon={<CheckIcon />}
                disabled={isApprovalDisabled}
                variant='contained'
                color='primary'
                size='large'
            >
                Aprovar
            </CustomButton>
        </Paper>
    )
}

interface EditButtonProps {
    isDisabled: boolean
    handleEdit: () => void
}
function EditButton({ isDisabled, handleEdit }: Readonly<EditButtonProps>): React.JSX.Element {
    return (
        <Paper
            square
            sx={{
                boxShadow: 'none',
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                backgroundColor: 'transparent',
                marginTop: '0.5rem',
            }}
        >
            <CustomButton
                variant='contained'
                size='large'
                onClick={handleEdit}
                disabled={isDisabled}
                endIcon={<CheckIcon />}
            >
                Guardar Alterações
            </CustomButton>
        </Paper>
    )
}
